import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import FolderIcon from '@mui/icons-material/Folder'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'

import {
  ResourcesContext,
  ResourcesContextInterface,
} from '../../../providers/Resources/Resources.provider'
import { themeColors } from '../../../theme'
import { ResourceFile, ResourceFolder } from './Resources.view'

interface Props {
  fileToMove: ResourceFile | null
  moveModalOpen: boolean
  setMoveModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  setFileToMove: React.Dispatch<React.SetStateAction<ResourceFile | null>>
  fetchFolderContent: (folderId: number) => Promise<void>
}

interface FolderOption {
  id: number
  name: string
  parentFolderId: number | null
}

export const MoveResourceFileModal: React.FC<Props> = ({
  fileToMove,
  moveModalOpen,
  setMoveModalOpen,
  setFileToMove,
  fetchFolderContent,
}) => {
  const { rootResourceFolders, getFolderContentById, moveResourceFile } =
    useContext(ResourcesContext) as ResourcesContextInterface

  const [folders, setFolders] = useState<FolderOption[]>([])
  const [folderPath, setFolderPath] = useState<FolderOption[]>([])
  const [moving, setMoving] = useState(false)

  useEffect(() => {
    if (moveModalOpen) {
      setFolders(rootResourceFolders ?? [])
      setFolderPath([])
    }
  }, [moveModalOpen, rootResourceFolders])

  const currentFolder =
    folderPath.length > 0 ? folderPath[folderPath.length - 1] : null

  const openFolder = async (folder: FolderOption) => {
    const response = await getFolderContentById(folder.id)

    const subfolders = response.contents.filter(
      (item: ResourceFolder) => item.type === 'folder'
    )

    setFolders(subfolders)
    setFolderPath((prev) => [...prev, folder])
  }

  const goBack = async () => {
    if (folderPath.length <= 1) {
      setFolderPath([])
      setFolders(rootResourceFolders ?? [])
      return
    }

    const newPath = folderPath.slice(0, -1)
    const parent = newPath[newPath.length - 1]

    const response = await getFolderContentById(parent.id)

    setFolders(
      response.contents.filter((item: ResourceFolder) => item.type === 'folder')
    )

    setFolderPath(newPath)
  }

  const handleMove = async () => {
    if (!fileToMove || !currentFolder) return

    try {
      setMoving(true)

      await moveResourceFile(fileToMove.id, currentFolder.id)

      setMoveModalOpen(false)
      setFileToMove(null)

      await fetchFolderContent(fileToMove.folderId)
    } finally {
      setMoving(false)
    }
  }

  return (
    <Dialog
      open={moveModalOpen}
      onClose={() => setMoveModalOpen(false)}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle style={{ fontSize: '18px' }}>
        Move{' '}
        <span
          style={{
            color: themeColors.indigo,
            fontSize: '20px',
          }}
        >
          {fileToMove?.name}
        </span>{' '}
        to:{' '}
        <Typography fontSize={14}>
          {folderPath.length ? folderPath.map((f) => f.name).join(' / ') : ''}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {folderPath.length > 0 && (
          <Button startIcon={<ArrowBackIcon />} onClick={goBack}>
            Back
          </Button>
        )}

        <List>
          {folders.map((folder) => (
            <ListItemButton key={folder.id} onClick={() => openFolder(folder)}>
              <ListItemIcon>
                <FolderIcon color='primary' />
              </ListItemIcon>

              <ListItemText primary={folder.name} />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setMoveModalOpen(false)}>Cancel</Button>

        <Button
          variant='contained'
          disabled={
            moving ||
            !currentFolder ||
            currentFolder.id === fileToMove?.folderId
          }
          onClick={handleMove}
        >
          Move Here
        </Button>
      </DialogActions>
    </Dialog>
  )
}
